import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CopyToClipboard } from '@delmonicos/shared-design-system-react/src/components/copy-to-clipboard/copy-to-clipboard';
import {
  Divider,
  DividerOrientation,
} from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { NumberFixed } from '@delmonicos/shared-design-system-react/src/components/number-fixed/number-fixed';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { toCurrencyString } from '@delmonicos/shared-sdk/libs/core/src/utils/formatters/currency.formatters';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { getDuration } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/get-duration';
import { TransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/transaction.model';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';

interface TransactionCardComponentProps {
  transaction: TransactionModel;
}

export const TransactionCardComponent = ({
  transaction,
}: TransactionCardComponentProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <Card>
      <Stack gap={GapEnum.M} flex={1}>
        <Stack gap={GapEnum.M} flex={1}>
          chart
        </Stack>
        <Divider orientation={DividerOrientation.Vertical} />
        <Stack gap={GapEnum.M} direction="column" flex={1}>
          <Stack
            gap={GapEnum.L}
            justifyContent="space-between"
            alignItems="center">
            <Text color={TextColor.Secondary} fontSize={TextFontSize.Medium}>
              {t('$.transaction-details.transaction-id')}
            </Text>
            <Stack>
              <CopyToClipboard textToCopy={transaction.ocpiSessionId}>
                <Text
                  color={TextColor.Secondary}
                  fontSize={TextFontSize.Medium}
                  truncate>
                  {transaction.ocpiSessionId ?? '-'}
                </Text>
              </CopyToClipboard>
            </Stack>
          </Stack>
          <Stack
            gap={GapEnum.L}
            justifyContent="space-between"
            alignItems="center">
            <Stack gap={GapEnum.S} alignItems="center" flex={1}>
              <Icon name="clock-fast-forward" />
              <Text bold>
                {t('delmonicos-shared-sdk.outputs-transaction.duration')}
              </Text>
            </Stack>
            <Stack flex={1}>
              <Tag className="bg-surface-secondary" flex={1}>
                {getDuration(transaction.startedAt, transaction.stoppedAt)}
              </Tag>
            </Stack>
          </Stack>
          <Stack gap={GapEnum.L} alignItems="center">
            <Stack
              gap={GapEnum.S}
              justifyContent="space-between"
              flex={1}
              alignItems="center">
              <Stack gap={GapEnum.S} alignItems="center" flex={1}>
                <Icon name="calendar" />
                <Text bold>
                  {t('delmonicos-shared-sdk.outputs-transaction.started-at')}
                </Text>
              </Stack>
              <Text>{formatDate(transaction.startedAt, i18n)}</Text>
            </Stack>
            <Stack
              flex={1}
              gap={GapEnum.S}
              justifyContent="space-between"
              alignItems="center">
              <Stack gap={GapEnum.S} alignItems="center" flex={1}>
                <Icon name="calendar-check-02" />
                <Text bold>
                  {t('delmonicos-shared-sdk.outputs-transaction.stopped-at')}
                </Text>
              </Stack>
              <Text>
                {transaction.stoppedAt
                  ? formatDate(transaction.stoppedAt, i18n)
                  : '-'}
              </Text>
            </Stack>
          </Stack>
          <Divider />
          <Stack gap={GapEnum.L} alignItems="center">
            <Stack
              gap={GapEnum.S}
              justifyContent="space-between"
              flex={1}
              alignItems="center">
              <Stack gap={GapEnum.S} alignItems="center" flex={1}>
                <Icon name="currency-euro-circle" />
                <Text bold>
                  {t('delmonicos-shared-sdk.outputs-transaction.total-cost')}
                </Text>
              </Stack>
              <Tag className="bg-surface-secondary" flex={1}>
                {toCurrencyString(transaction.totalCost, transaction.currency)}
              </Tag>
            </Stack>
            <Stack
              flex={1}
              gap={GapEnum.S}
              justifyContent="space-between"
              alignItems="center">
              <Stack gap={GapEnum.S} alignItems="center" flex={1}>
                <Icon name="lightning-02" />
                <Text bold>
                  {t('delmonicos-shared-sdk.outputs-transaction.kwh')}
                </Text>
              </Stack>
              <Tag className="bg-surface-secondary" flex={1}>
                <NumberFixed number={transaction.kwh}>kWh</NumberFixed>
              </Tag>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
